import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { downloadInvoicePDF, createPDFPreviewURL } from './invoice-pdf';

// インセンティブ明細の1行データ
export interface IncentiveDetailItem {
  constructionNumber: string;
  companyName: string;
  title: string;
  amount: number;
  role: 'assigned' | 'pm' | 'promoter';
}

// ユーザーごとのインセンティブ明細データ
export interface IncentiveStatementData {
  departmentName: string;
  userName: string;
  paymentDate: string; // 支給日（例: 2026/1/10）
  details: IncentiveDetailItem[];
  totalAmount: number;
  createdDate: string; // 作成日（例: 2026年1月8日）
}

// 金額フォーマット
function formatYen(amount: number): string {
  return `¥${Math.abs(amount).toLocaleString('ja-JP')}`;
}

// 日付フォーマット（支給日用: 2026/1/10支給）
function formatPaymentDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}支給`;
}

// 日付フォーマット（作成日用: 2026年1月8日）
function formatCreatedDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

// テンプレートに合わせたHTMLを生成
function createIncentiveStatementHTML(data: IncentiveStatementData): string {
  const detailRows = data.details.map(item => {
    const amountStr = item.amount < 0
      ? `<span style="color: red;">-${formatYen(item.amount)}</span>`
      : formatYen(item.amount);

    return `
      <tr>
        <td style="border: 1px solid #999; padding: 6px 8px; font-size: 12px; white-space: nowrap;">${item.constructionNumber}</td>
        <td style="border: 1px solid #999; padding: 6px 8px; font-size: 12px;">${item.companyName}</td>
        <td style="border: 1px solid #999; padding: 6px 8px; font-size: 12px;">${item.title}</td>
        <td style="border: 1px solid #999; padding: 6px 8px; font-size: 12px; text-align: right; white-space: nowrap;"></td>
        <td style="border: 1px solid #999; padding: 6px 8px; font-size: 12px; text-align: right; white-space: nowrap;">${amountStr}</td>
      </tr>
    `;
  }).join('');

  return `
    <div id="incentive-statement-content" style="width: 794px; padding: 40px 40px 30px 40px; font-family: 'Hiragino Kaku Gothic ProN', 'Meiryo', 'Yu Gothic', sans-serif; background: white; color: #333;">
      <!-- 支給日 -->
      <div style="text-align: left; margin-bottom: 8px;">
        <span style="font-size: 13px; color: #333;">${formatPaymentDate(data.paymentDate)}</span>
      </div>

      <!-- タイトル -->
      <div style="text-align: center; margin-bottom: 20px;">
        <h1 style="font-size: 22px; margin: 0; font-weight: bold; letter-spacing: 4px;">インセンティブ明細</h1>
      </div>

      <!-- ヘッダーテーブル（部署、氏名、合計） -->
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 16px;">
        <tr>
          <td style="border: 1px solid #999; padding: 8px 12px; font-size: 13px; background: #f5f5f5; width: 18%; font-weight: bold;">${data.departmentName}</td>
          <td style="border: 1px solid #999; padding: 8px 12px; font-size: 14px; font-weight: bold; width: 32%;">${data.userName}</td>
          <td style="border: 1px solid #999; padding: 8px 12px; font-size: 13px; width: 10%;"></td>
          <td style="border: 1px solid #999; padding: 8px 12px; font-size: 13px; background: #f5f5f5; text-align: center; width: 12%; font-weight: bold;">合計</td>
          <td style="border: 1px solid #999; padding: 8px 12px; font-size: 16px; font-weight: bold; text-align: right; width: 28%;">${formatYen(data.totalAmount)}</td>
        </tr>
      </table>

      <!-- 明細テーブル -->
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 24px;">
        <thead>
          <tr style="background: #f5f5f5;">
            <th style="border: 1px solid #999; padding: 6px 8px; font-size: 11px; text-align: center; width: 15%;">案件番号</th>
            <th style="border: 1px solid #999; padding: 6px 8px; font-size: 11px; text-align: center; width: 28%;">顧客名</th>
            <th style="border: 1px solid #999; padding: 6px 8px; font-size: 11px; text-align: center; width: 32%;">工事内容</th>
            <th style="border: 1px solid #999; padding: 6px 8px; font-size: 11px; text-align: center; width: 10%;"></th>
            <th style="border: 1px solid #999; padding: 6px 8px; font-size: 11px; text-align: center; width: 15%;">金額</th>
          </tr>
        </thead>
        <tbody>
          ${detailRows}
        </tbody>
      </table>

      <!-- 作成日 -->
      <div style="text-align: right; margin-top: 10px;">
        <span style="font-size: 13px; color: #333;">${formatCreatedDate(data.createdDate)}</span>
      </div>
    </div>
  `;
}

// インセンティブ明細PDFを生成
export async function generateIncentiveStatementPDF(
  data: IncentiveStatementData
): Promise<{ blob: Blob; base64: string }> {
  const html = createIncentiveStatementHTML(data);

  const container = document.createElement('div');
  container.innerHTML = html;
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  document.body.appendChild(container);

  const element = container.querySelector('#incentive-statement-content') as HTMLElement;

  try {
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    });

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const imgWidth = pageWidth - 20;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let finalWidth = imgWidth;
    let finalHeight = imgHeight;

    if (imgHeight > pageHeight - 20) {
      finalHeight = pageHeight - 20;
      finalWidth = (canvas.width * finalHeight) / canvas.height;
    }

    const imgData = canvas.toDataURL('image/png');
    doc.addImage(imgData, 'PNG', 10, 10, finalWidth, finalHeight);

    const blob = doc.output('blob');
    const base64 = doc.output('datauristring').split(',')[1];

    return { blob, base64 };
  } finally {
    document.body.removeChild(container);
  }
}

// re-export共通ユーティリティ
export { downloadInvoicePDF as downloadIncentiveStatementPDF, createPDFPreviewURL } from './invoice-pdf';
